import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Routes, Filters, GaAnalyticEvents } from '../../constants';
import {
  getUserLocation,
  getUserLocationByIp,
  getUserAddressByCoords,
  isNotAllowedToCallGeoApi,
  getBaiduUserAddressByCoords,
  getYandexUserAddressByCoords,
} from '../../utils/geocodeUtils';
import {
  gtagPushEvent,
  trackEvent,
  trackPageView,
} from '../../utils/analyticUtils';
import {
  generateUrlPayload,
  stringifyQueryStrings,
} from '../../utils/urlParamUtils';
import Session from '../../session';
import bemify from '../../utils/bemUtils';
import HomeSkeleton from './homeSkeleton';
import Map from '../../components/map/map';
import BaiduMap from '../../components/baiduMap/baiduMap';
import withConfig from '../../hoc/withConfig';
import withRouter from '../../hoc/withRouter';
import Search from '../../components/search/search';
import { SessionStorageKeys } from '../../constants';
import { debounce } from '../../utils/debounceUtils';
import { generateRoute } from '../../utils/routeUtils';
import ButtonTiles from '../../components/buttonTiles/buttonTiles';
import TenantService from '../../services/tenantService';
import HomeTabViewSkeleton from './homeTabViewSkeleton';
import { formatYandexAddress, pluckBaiduCityState, pluckCityState, pluckYandexCityState } from '../../utils/searchUtils';
import { checkForUserRedirection } from '../../utils/redirectionUtils';
import YandexMap2 from '../../components/yandexMap/yandexMap2';

/**
 * Represents the home page of the app.
 */
class Home extends Component {
  /**
   * Initializes a new instance of the Home component.
   * @param {Object} props The component properties.
   */
  constructor(props) {
    super(props);

    this.state = {
      isReadyToRender: false,
      searchLocationCoords: {
        lat: 0,
        lng: 0,
      },
      isFirstLoad: false,
    };
    this._onSafeWindowResize = debounce(() => this._onWindowResize(), 250);
    this._tenantService = new TenantService();
  }

  /**
   * Executes when the component has mounted to the DOM.
   */
  async componentDidMount() {
    const { config } = this.props;

    await checkForUserRedirection(config);

    trackPageView(config, '/landing');

    window.dataLayer = window.dataLayer || [];

    gtagPushEvent({
      event: 'Pageview',
      pagePath: window.location.href,
      pageTitle: 'Eye doctor landing page',
    });

    this.setState({
      isFirstLoad: true,
    });

    const { coordinates } = window.locatorTenancy;

    if (coordinates) {
      await this._getUserLocationByCoords(coordinates);
    } else {
      const { search, hash } = this.props.location;

      if (isNotAllowedToCallGeoApi(search, hash)) {
        await this._getUserLocationByIp();
      } else {
        await this._getUserLocation();
      }
    }

    window.addEventListener('resize', this._onSafeWindowResize);

    this._onSafeWindowResize();
  }

  /**
   * Executes when its time for the component to unmount from the DOM.
   */
  componentWillUnmount() {
    window.removeEventListener('resize', this._onSafeWindowResize);
  }

  _getUserLocation = async () => {
    try {
      const { config } = this.props;
      const { use_baidu_map, use_yandex_map } = config;
      let shortenedAddress = '';
      const coords = await getUserLocation();

      if (use_yandex_map) {
        const { featureMember } = await getYandexUserAddressByCoords(coords);
        shortenedAddress = pluckYandexCityState(featureMember);
      } else if (use_baidu_map) {
        const { addressComponent } = await getBaiduUserAddressByCoords(coords);
        shortenedAddress = pluckBaiduCityState(addressComponent);
      } else {
        const { components } = await getUserAddressByCoords(coords);
        shortenedAddress = pluckCityState(components);
      }

      const payload = generateUrlPayload(config, coords, shortenedAddress, this.props.location);

      if (payload.isFirstLoad) {
        this.setState({
          isFirstLoad: false,
        });
      }

      payload.isFirstLoad = this.state.isFirstLoad;

      const selectedFilters = Session.getItem(SessionStorageKeys.selectedFilters);
      const isOnlineBookingEnabled = !!selectedFilters?.some(
        (f) => f.key === Filters.onlineBooking
      );

      // TODO: place session based filters here
      const sessionFilters = {};
      if (isOnlineBookingEnabled) {
        payload.filteredSearch = 1;
        payload['Online Booking'] = 'Yes';
      }

      const queryStrings = stringifyQueryStrings(payload);
      const resultsRoute = generateRoute(config, Routes.results);

      trackEvent(GaAnalyticEvents.search.allowLocation);

      Session.setItem(SessionStorageKeys.isGeo, true);
      Session.setItem(SessionStorageKeys.allowLocationAccess, true);
      Session.setItem(SessionStorageKeys.userCurrentLocation, {
        lat: coords.latitude,
        long: coords.longitude,
      });

      const { navigate } = this.props;
      navigate(`/${resultsRoute}?${queryStrings}`);
    } catch {
      // User denied allowing us to get their location.
      // We'll use their IP to geo locate them instead.
      trackEvent(GaAnalyticEvents.search.declineLocation);

      await this._getUserLocationByIp();
    }
  };

  _getUserLocationByCoords = async (coords) => {
    try {
      const { config, navigate } = this.props;
      const { use_baidu_map, use_yandex_map } = config;
      let userAddress = '';

      if (use_baidu_map) {
        const { formatted_address } = await getBaiduUserAddressByCoords(coords);
        userAddress = formatted_address;
      } else if (use_yandex_map) {
        const { featureMember } = await getYandexUserAddressByCoords(coords);
        userAddress = formatYandexAddress(featureMember);
      } else {
        const { address } = await getUserAddressByCoords(coords);
        userAddress = address;
      }

      const urlPayload = generateUrlPayload(config, coords, userAddress, this.props.location);
      const queryStringValues = stringifyQueryStrings(urlPayload);

      Session.setItem(SessionStorageKeys.isGeo, true);
      Session.setItem(SessionStorageKeys.allowLocationAccess, true);
      Session.setItem(SessionStorageKeys.userCurrentLocation, {
        lat: coords.latitude,
        long: coords.longitude,
      });

      const resultsPath = generateRoute(config, 'result');
      navigate(`./${resultsPath}?${queryStringValues}`);
    } catch (error) {
      console.error(error);
    }
  };

  _getUserLocationByIp = async () => {
    const res = await getUserLocationByIp();
    const location = res ? res : { country_code: 'BR' };

    Session.setItem(SessionStorageKeys.isGeo, true);
    Session.setItem(SessionStorageKeys.allowLocationAccess, false);
    Session.setItem(SessionStorageKeys.userCurrentLocation, {
      lat: location.latitude,
      long: location.longitude,
    });

    if (location?.city && location?.region) {
      this.setState(() => ({
        isReadyToRender: true,
        searchLocationCoords: {
          lat: res.latitude,
          lng: res.longitude,
        },
      }));
    } else {
      this.setState(() => ({
        searchLocationCoords: {
          lat: 0,
          lng: 0,
        },
      }));
    }
  };

  _onWindowResize = () => {
    const { body, documentElement } = document;
    const { innerHeight } = window;
    body.style.height = innerHeight;
    documentElement.style.height = `${innerHeight}px`;
    documentElement.style.setProperty('--map-height', `${innerHeight}px`);
  };

  /**
   *
   * @param {boolean} forceTabView
   * @returns JSX skeleton
   */
  _getSkeleton = (forceTabView) =>
    forceTabView ? <HomeTabViewSkeleton /> : <HomeSkeleton />;

  /**
   * Renders the component.
   */
  render() {
    const { isReadyToRender, searchLocationCoords, isFirstLoad } = this.state;
    const { t, config } = this.props;
    const { use_mobile_view, enable_rtl_orientation, use_baidu_map, use_yandex_map } = config;
    const homeLayout = use_mobile_view ? 'homeMobile' : 'home';
    const [block, element] = bemify(homeLayout);
    const isHomePage = true;

    return (
      <>
        {isReadyToRender && (
          <div
            className={`${block()}${enable_rtl_orientation ? '--flipped' : ''}`}
          >
            <section
              className={element(
                'search',
                enable_rtl_orientation ? 'flipped' : ''
              )}
            >
              <h1 className={element('title')}>{t('text_landing_headline')}</h1>
              <Search isFirstLoad={isFirstLoad} isHomePage={isHomePage}/>
              <ButtonTiles />
            </section>
            {use_baidu_map ? (
              <section className={element('baidu-map')}>
                <BaiduMap
                  searchLocationCoords={searchLocationCoords}
                  isFirstLoad={isFirstLoad}
                />
              </section>
            ) : use_yandex_map ? (
              <section className={element('yandex-map')}>
                <YandexMap2
                  searchLocationCoords={searchLocationCoords}
                  isFirstLoad={isFirstLoad}
                />
              </section>
            ) : (
              <section className={element('map')}>
                <Map
                  searchLocationCoords={searchLocationCoords}
                  isFirstLoad={isFirstLoad}
                />
              </section>
            )}
          </div>
        )}
        {!isReadyToRender && this._getSkeleton(use_mobile_view)}
      </>
    );
  }
}

Home.propTypes = {
  t: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
};

export default withConfig(withRouter(withTranslation()(Home)));
